import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  CircleHelp,
  Code2,
  FileAudio,
  FileText,
  FileVideo,
  Files,
  Image as ImageIcon,
  Link2,
  MessageSquareWarning,
  NotebookPen,
  PlaySquare,
  Trash2,
  Upload,
} from 'lucide-react';
import { CALLOUT_VARIANTS, skillLabel, type Block, type BlockType } from '@ready/shared';
import { useApi, type ContentOwner } from '@/lib/api';
import { Button, Card, Field, Input, Select, Textarea } from '@/components/ui';
import { CODE_KINDS, DOCUMENT_KINDS, FilePicker, FileSummary, useCourseFiles } from './file-picker';
import { useUpload } from './upload';
import { AiButton, useAiEnabled } from '@/components/ai/ai-assist';
import { QuestionAssist } from '@/components/ai/question-assist';

/*
 * Editors for each lesson block type. Every block is edited in place in a
 * card with move/delete controls; file blocks upload or reuse course files.
 */

export const newBlockId = () => Math.random().toString(36).slice(2, 10);

export const BLOCK_TYPES: Array<{
  type: BlockType;
  label: string;
  hint: string;
  icon: ReactNode;
  create: () => Block | null;
}> = [
  {
    type: 'markdown',
    label: 'Text',
    hint: 'Markdown: headings, lists, code, tables, math, diagrams',
    icon: <AlignLeft className="size-5" />,
    create: () => ({ id: newBlockId(), type: 'markdown', markdown: '' }),
  },
  {
    type: 'video',
    label: 'Video',
    hint: 'Upload a short lecture (MP4, up to 500 MB)',
    icon: <FileVideo className="size-5" />,
    create: () => null,
  },
  {
    type: 'audio',
    label: 'Audio',
    hint: 'Upload a recording (MP3/M4A)',
    icon: <FileAudio className="size-5" />,
    create: () => null,
  },
  {
    type: 'document',
    label: 'Slides or document',
    hint: 'PDF, PowerPoint, Word or Excel, shown inline',
    icon: <FileText className="size-5" />,
    create: () => null,
  },
  {
    type: 'embed',
    label: 'Embed',
    hint: 'YouTube, Vimeo, Loom, Google Slides/Docs, Figma, CodePen',
    icon: <PlaySquare className="size-5" />,
    create: () => ({ id: newBlockId(), type: 'embed', url: '' }) as unknown as Block,
  },
  {
    type: 'link',
    label: 'Link',
    hint: 'A website or article, shown as a card',
    icon: <Link2 className="size-5" />,
    create: () => ({ id: newBlockId(), type: 'link', url: '' }) as unknown as Block,
  },
  {
    type: 'image',
    label: 'Image',
    hint: 'A diagram or photo with alt text',
    icon: <ImageIcon className="size-5" />,
    create: () => null,
  },
  {
    type: 'notebook',
    label: 'Notebook',
    hint: 'A Jupyter notebook (.ipynb), rendered',
    icon: <NotebookPen className="size-5" />,
    create: () => null,
  },
  {
    type: 'code',
    label: 'Code',
    hint: 'A code file or snippet, highlighted',
    icon: <Code2 className="size-5" />,
    create: () => ({ id: newBlockId(), type: 'code', source: '', language: 'python' }),
  },
  {
    type: 'files',
    label: 'Downloads',
    hint: 'Files learners can download',
    icon: <Files className="size-5" />,
    create: () => null,
  },
  {
    type: 'callout',
    label: 'Callout',
    hint: 'A tip, note or warning',
    icon: <MessageSquareWarning className="size-5" />,
    create: () => ({ id: newBlockId(), type: 'callout', variant: 'tip', markdown: '' }),
  },
  {
    type: 'knowledge_check',
    label: 'Knowledge check',
    hint: 'Practice questions from a question bank',
    icon: <CircleHelp className="size-5" />,
    create: () =>
      ({ id: newBlockId(), type: 'knowledge_check', questionIds: [] }) as unknown as Block,
  },
];
export const blockLabel = (type: BlockType) =>
  BLOCK_TYPES.find((b) => b.type === type)?.label ?? type;

/** A block that needs a file before it's valid starts as a placeholder. */
export type DraftBlock = Block | { id: string; type: BlockType; pending: true };
export const isPending = (b: DraftBlock): b is { id: string; type: BlockType; pending: true } =>
  'pending' in b;

export function BlockCard({
  block,
  index,
  count,
  onChange,
  onMove,
  onRemove,
  owner,
  courseId,
  lessonId,
}: {
  block: DraftBlock;
  index: number;
  count: number;
  onChange: (b: DraftBlock) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  owner: ContentOwner;
  courseId: string;
  /** The lesson being edited; AI assist drafts knowledge checks from it. */
  lessonId?: string;
}) {
  const meta = BLOCK_TYPES.find((b) => b.type === block.type);
  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-primary" aria-hidden>
          {meta?.icon}
        </span>
        <h3 className="min-w-0 flex-1 truncate font-medium">{meta?.label ?? block.type}</h3>
        <IconButton label="Move up" disabled={index === 0} onClick={() => onMove(-1)}>
          <ArrowUp className="size-4" />
        </IconButton>
        <IconButton label="Move down" disabled={index === count - 1} onClick={() => onMove(1)}>
          <ArrowDown className="size-4" />
        </IconButton>
        <IconButton
          label="Delete block"
          onClick={() =>
            window.confirm(`Delete this ${meta?.label.toLowerCase() ?? 'block'}?`) && onRemove()
          }
        >
          <Trash2 className="size-4" />
        </IconButton>
      </div>
      <BlockFields
        block={block}
        onChange={onChange}
        owner={owner}
        courseId={courseId}
        lessonId={lessonId}
      />
    </Card>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function BlockFields({
  block,
  onChange,
  owner,
  courseId,
  lessonId,
}: {
  block: DraftBlock;
  onChange: (b: DraftBlock) => void;
  owner: ContentOwner;
  lessonId?: string;
  courseId: string;
}) {
  const id = block.id;
  const set = (patch: Record<string, unknown>) =>
    onChange({ ...(block as Block), ...patch } as Block);
  const pickFile = (kinds: Parameters<typeof FilePicker>[0]['kinds'], label: string) => (
    <FilePicker
      owner={owner}
      courseId={courseId}
      kinds={kinds}
      label={label}
      value={isPending(block) ? null : ((block as { assetId?: string }).assetId ?? null)}
      onChange={(assetId) =>
        onChange({ ...(isPending(block) ? { id, type: block.type } : block), assetId } as Block)
      }
    />
  );
  const titleField = (b: Block) => (
    <Field label="Title (optional)" htmlFor={`${id}-title`}>
      <Input
        id={`${id}-title`}
        value={(b as { title?: string }).title ?? ''}
        onChange={(e) => set({ title: e.target.value || undefined })}
      />
    </Field>
  );

  switch (block.type) {
    case 'markdown':
      return (
        <MarkdownField
          id={id}
          value={(block as Extract<Block, { type: 'markdown' }>).markdown ?? ''}
          onChange={(markdown) => set({ markdown })}
        />
      );
    case 'callout': {
      const b = block as Extract<Block, { type: 'callout' }>;
      return (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Style" htmlFor={`${id}-variant`}>
              <Select
                id={`${id}-variant`}
                value={b.variant ?? 'info'}
                onChange={(e) => set({ variant: e.target.value })}
              >
                {CALLOUT_VARIANTS.map((v) => (
                  <option key={v} value={v}>
                    {v === 'info'
                      ? 'Note'
                      : v === 'tip'
                        ? 'Tip'
                        : v === 'warning'
                          ? 'Warning'
                          : 'Success'}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Title (optional)" htmlFor={`${id}-title`}>
              <Input
                id={`${id}-title`}
                value={b.title ?? ''}
                onChange={(e) => set({ title: e.target.value || undefined })}
              />
            </Field>
          </div>
          <MarkdownField
            id={id}
            value={b.markdown ?? ''}
            onChange={(markdown) => set({ markdown })}
            rows={4}
          />
        </div>
      );
    }
    case 'video':
    case 'audio':
      return (
        <div className="space-y-3">
          {pickFile([block.type], block.type)}
          {!isPending(block) && titleField(block as Block)}
        </div>
      );
    case 'image': {
      const b = block as Partial<Extract<Block, { type: 'image' }>>;
      return (
        <div className="space-y-3">
          {pickFile(['image'], 'image')}
          {!isPending(block) && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Alt text (describe the image)" htmlFor={`${id}-alt`}>
                <Input
                  id={`${id}-alt`}
                  required
                  value={b.alt ?? ''}
                  onChange={(e) => set({ alt: e.target.value })}
                />
              </Field>
              <Field label="Caption (optional)" htmlFor={`${id}-caption`}>
                <Input
                  id={`${id}-caption`}
                  value={b.caption ?? ''}
                  onChange={(e) => set({ caption: e.target.value || undefined })}
                />
              </Field>
            </div>
          )}
        </div>
      );
    }
    case 'document': {
      const b = block as Partial<Extract<Block, { type: 'document' }>>;
      return (
        <div className="space-y-3">
          {pickFile(DOCUMENT_KINDS, 'file')}
          {!isPending(block) && (
            <>
              {titleField(block as Block)}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-5 accent-primary"
                  checked={b.allowDownload ?? true}
                  onChange={(e) => set({ allowDownload: e.target.checked })}
                />
                Let learners download the original file
              </label>
            </>
          )}
        </div>
      );
    }
    case 'notebook':
      return (
        <div className="space-y-3">
          {pickFile(['notebook'], 'notebook')}
          {!isPending(block) && titleField(block as Block)}
        </div>
      );
    case 'code': {
      const b = block as Extract<Block, { type: 'code' }>;
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste code below, or use an uploaded file instead.
          </p>
          <FilePicker
            owner={owner}
            courseId={courseId}
            kinds={CODE_KINDS}
            label="code file"
            value={b.assetId ?? null}
            onChange={(assetId) => set({ assetId, source: undefined })}
          />
          {!b.assetId && (
            <Textarea
              aria-label="Code"
              rows={8}
              className="font-mono text-sm"
              value={b.source ?? ''}
              onChange={(e) => set({ source: e.target.value })}
            />
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="File name (optional)" htmlFor={`${id}-filename`}>
              <Input
                id={`${id}-filename`}
                placeholder="agent.py"
                value={b.filename ?? ''}
                onChange={(e) => set({ filename: e.target.value || undefined })}
              />
            </Field>
            <Field label="Language" htmlFor={`${id}-lang`}>
              <Input
                id={`${id}-lang`}
                placeholder="python"
                value={b.language ?? ''}
                onChange={(e) => set({ language: e.target.value || undefined })}
              />
            </Field>
          </div>
          {b.assetId && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => set({ assetId: undefined, source: '' })}
            >
              Paste code instead
            </Button>
          )}
        </div>
      );
    }
    case 'files':
      return <FilesField block={block} onChange={onChange} owner={owner} courseId={courseId} />;
    case 'embed': {
      const b = block as Extract<Block, { type: 'embed' }>;
      return (
        <div className="space-y-3">
          <Field
            label="Link to embed"
            htmlFor={`${id}-url`}
            hint="YouTube, Vimeo, Loom, Google Slides/Docs/Sheets/Forms, Figma or CodePen. Other sites show as a link card."
          >
            <Input
              id={`${id}-url`}
              type="url"
              required
              placeholder="https://www.youtube.com/watch?v=…"
              value={b.url ?? ''}
              onChange={(e) => set({ url: e.target.value })}
            />
          </Field>
          {titleField(b)}
        </div>
      );
    }
    case 'link': {
      const b = block as Extract<Block, { type: 'link' }>;
      return <LinkFields block={b} set={set} />;
    }
    case 'knowledge_check':
      return (
        <KnowledgeCheckField
          block={block as Extract<Block, { type: 'knowledge_check' }>}
          set={set}
          owner={owner}
          lessonId={lessonId}
        />
      );
    default:
      return null;
  }
}

/** Markdown with an "import a .md file" shortcut. (Preview comes from the lesson preview.) */
function MarkdownField({
  id,
  value,
  onChange,
  rows = 10,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      <Textarea
        aria-label="Text (Markdown)"
        id={`${id}-md`}
        rows={rows}
        className="font-mono text-sm"
        placeholder={
          '## Heading\n\nWrite with **Markdown**: lists, `code`, tables, $math$ and ```mermaid diagrams.'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <input
        ref={input}
        type="file"
        accept=".md,.mdx,.markdown,.txt"
        className="sr-only"
        aria-label="Import a Markdown file"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          const text = await file.text();
          if (!value.trim() || window.confirm('Replace the current text with this file?'))
            onChange(text);
        }}
      />
      <Button type="button" variant="ghost" onClick={() => input.current?.click()}>
        <Upload className="size-4" aria-hidden /> Import a .md file
      </Button>
    </div>
  );
}

function FilesField({
  block,
  onChange,
  owner,
  courseId,
}: {
  block: DraftBlock;
  onChange: (b: DraftBlock) => void;
  owner: ContentOwner;
  courseId: string;
}) {
  const files = useCourseFiles(owner);
  const { upload, state } = useUpload(owner, courseId);
  const input = useRef<HTMLInputElement>(null);
  const ids = isPending(block) ? [] : (block as Extract<Block, { type: 'files' }>).assetIds;
  const title = isPending(block) ? '' : ((block as Extract<Block, { type: 'files' }>).title ?? '');
  const save = (assetIds: string[], t = title) =>
    onChange(
      assetIds.length
        ? ({ id: block.id, type: 'files', assetIds, title: t || undefined } as Block)
        : { id: block.id, type: 'files', pending: true },
    );
  const chosen = ids.map((i) => files.data?.files.find((f) => f.id === i)).filter(Boolean);
  const available = (files.data?.files ?? []).filter(
    (f) => !ids.includes(f.id) && f.status !== 'failed',
  );
  return (
    <div className="space-y-3">
      <Field label="Title (optional)" htmlFor={`${block.id}-title`}>
        <Input
          id={`${block.id}-title`}
          placeholder="Resources for this lesson"
          value={title}
          onChange={(e) => save(ids, e.target.value)}
        />
      </Field>
      <ul className="space-y-2">
        {chosen.map((f) => (
          <li key={f!.id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <FileSummary file={f!} />
            </div>
            <IconButton
              label={`Remove ${f!.filename}`}
              onClick={() => save(ids.filter((x) => x !== f!.id))}
            >
              <Trash2 className="size-4" />
            </IconButton>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={input}
          type="file"
          multiple
          className="sr-only"
          aria-label="Upload files"
          onChange={async (e) => {
            const list = [...(e.target.files ?? [])];
            e.target.value = '';
            const added: string[] = [];
            for (const f of list) {
              const done = await upload(f);
              if (done) added.push(done.id);
            }
            if (added.length) save([...ids, ...added]);
          }}
        />
        <Button type="button" variant="secondary" onClick={() => input.current?.click()}>
          <Upload className="size-4" aria-hidden /> Upload files
        </Button>
        {available.length > 0 && (
          <Select
            aria-label="Add an uploaded file"
            className="w-auto min-w-0 flex-1 sm:max-w-xs"
            value=""
            onChange={(e) => e.target.value && save([...ids, e.target.value])}
          >
            <option value="">Or add an uploaded file…</option>
            {available.map((f) => (
              <option key={f.id} value={f.id}>
                {f.filename}
              </option>
            ))}
          </Select>
        )}
      </div>
      {state && state.phase !== 'done' && (
        <p className="text-sm text-muted-foreground">
          {state.phase === 'error' ? state.error : `Uploading ${state.filename}…`}
        </p>
      )}
    </div>
  );
}

function LinkFields({
  block,
  set,
}: {
  block: Extract<Block, { type: 'link' }>;
  set: (p: Record<string, unknown>) => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = block.id;
  return (
    <div className="space-y-3">
      <Field label="Link" htmlFor={`${id}-url`}>
        <div className="flex gap-2">
          <Input
            id={`${id}-url`}
            type="url"
            required
            placeholder="https://…"
            value={block.url ?? ''}
            onChange={(e) => set({ url: e.target.value })}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={busy || !block.url}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const p = await api.linkPreview(block.url);
                set({
                  title: p.title ?? block.title,
                  description: p.description ?? block.description,
                  imageUrl: p.imageUrl ?? undefined,
                  siteName: p.siteName ?? undefined,
                });
              } catch {
                setError("Couldn't read that page. Fill in the details yourself.");
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Reading…' : 'Fill in'}
          </Button>
        </div>
      </Field>
      {error && <p className="text-sm text-muted-foreground">{error}</p>}
      <Field label="Title" htmlFor={`${id}-title`}>
        <Input
          id={`${id}-title`}
          value={block.title ?? ''}
          onChange={(e) => set({ title: e.target.value || undefined })}
        />
      </Field>
      <Field label="Description (optional)" htmlFor={`${id}-desc`}>
        <Textarea
          id={`${id}-desc`}
          rows={2}
          value={block.description ?? ''}
          onChange={(e) => set({ description: e.target.value || undefined })}
        />
      </Field>
    </div>
  );
}

function KnowledgeCheckField({
  block,
  set,
  owner,
  lessonId,
}: {
  block: Extract<Block, { type: 'knowledge_check' }>;
  set: (p: Record<string, unknown>) => void;
  owner: ContentOwner;
  lessonId?: string;
}) {
  const aiEnabled = useAiEnabled();
  const [assist, setAssist] = useState(false);
  const api = useApi();
  const banks = useQuery({
    queryKey: ['banks', owner.kind === 'org' ? owner.orgId : 'library'],
    queryFn: () => api.banks(owner),
  });
  const [bankId, setBankId] = useState('');
  const activeBank = banks.data?.banks.find((b) => b.id === bankId) ?? banks.data?.banks[0];
  const questions = useQuery({
    queryKey: ['bank', activeBank?.id, ''],
    queryFn: () => api.bank(activeBank!.id),
    enabled: Boolean(activeBank),
  });
  const ids = block.questionIds ?? [];
  const toggle = (qid: string) =>
    set({ questionIds: ids.includes(qid) ? ids.filter((x) => x !== qid) : [...ids, qid] });
  return (
    <div className="space-y-3">
      <Field label="Title (optional)" htmlFor={`${block.id}-title`}>
        <Input
          id={`${block.id}-title`}
          placeholder="Check your understanding"
          value={block.title ?? ''}
          onChange={(e) => set({ title: e.target.value || undefined })}
        />
      </Field>
      <p className="text-sm text-muted-foreground">
        {ids.length} question{ids.length === 1 ? '' : 's'} selected. Learners get feedback on each
        answer; it doesn't affect their certificate.
      </p>
      {assist && lessonId ? (
        <QuestionAssist
          owner={owner}
          lessonId={lessonId}
          title="Draft a knowledge check with AI"
          defaultCount={3}
          defaultTypes={['single', 'true_false']}
          defaultBankTitle="Knowledge checks"
          saveLabel="Save and add"
          onSaved={(qs, bank) => {
            set({ questionIds: [...ids, ...qs.map((q) => q.id)] });
            setBankId(bank.id);
          }}
          onClose={() => setAssist(false)}
        />
      ) : (
        aiEnabled &&
        lessonId && (
          <AiButton type="button" onClick={() => setAssist(true)}>
            Draft from this lesson with AI
          </AiButton>
        )
      )}
      {banks.data?.banks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Create a question bank first (or draft questions with AI), then pick questions here.
        </p>
      ) : (
        <>
          <Field label="Question bank" htmlFor={`${block.id}-bank`}>
            <Select
              id={`${block.id}-bank`}
              value={activeBank?.id ?? ''}
              onChange={(e) => setBankId(e.target.value)}
            >
              {banks.data?.banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title}
                  {b.editable ? '' : ' (Nirvik library)'}
                </option>
              ))}
            </Select>
          </Field>
          <ul className="max-h-80 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
            {questions.data?.questions.map((q) => (
              <li key={q.id}>
                <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 text-sm hover:bg-muted">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-5 shrink-0 accent-primary"
                    checked={ids.includes(q.id)}
                    onChange={() => toggle(q.id)}
                  />
                  <span>
                    {q.prompt}
                    <span className="block text-xs text-muted-foreground">
                      {skillLabel(q.skill)}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
